import { KpiComparisonSeries, KpiMetric, KpiPeriod, KpiQuery } from '@/services/kpis/kpis.types';

/* Périmètres (voir OrderPerformance côté backend) : standard = lignes device dans le taux à l'heure ;
 * exchange / hs_proposal / out_of_stock = exclusions comptées à part ; sav = retours SAV hors litiges ;
 * sav_litigation = retours SAV en litige (en cours, long, traité), hors taux SAV ;
 * no_deadline = lignes non expédiées sans échéance (listes seulement, instantané) */
export type OrderPerformanceScope =
  | 'standard'
  | 'exchange'
  | 'hs_proposal'
  | 'out_of_stock'
  | 'sav'
  | 'sav_litigation'
  | 'no_deadline';

export const SAV_SCOPES: OrderPerformanceScope[] = ['sav', 'sav_litigation'];

/* Étape d'un retour SAV = onglet du module SAV / RC */
export type OrderPerformanceSavStage =
  | 'not_received'
  | 'pending'
  | 'handled'
  | 'validated'
  | 'litigation'
  | 'long_litigation'
  | 'litigation_traited';

/* on_time = expédiée avant l'échéance ; late = expédiée après ; pending = non expédiée, échéance dépassée ;
 * open = non expédiée, échéance à venir (hors dénominateur) ; no_shipment = SAV clos sans réexpédition */
export type OrderPerformanceStatus = 'on_time' | 'late' | 'pending' | 'open' | 'no_shipment';

/* Filtre statut des listes ; not_on_time = late + pending */
export type OrderPerformanceStatusFilter = 'all' | OrderPerformanceStatus | 'not_on_time';

export interface OrderPerformanceMetrics {
  due: KpiMetric;
  on_time: KpiMetric;
  not_on_time: KpiMetric;
  late: KpiMetric;
  pending: KpiMetric;
  open: KpiMetric;
  no_shipment: KpiMetric;
  on_time_rate: KpiMetric;
  not_on_time_rate: KpiMetric;
  /* Retards en jours calendaires entiers (seule la date d'échéance compte, pas l'heure) */
  mean_delay_days: KpiMetric;
  median_delay_days: KpiMetric;
  /* Retard des lignes en retard : 1 j, 2-3 j, > 3 j */
  delay_buckets: { d1: number; d3: number; d3p: number };
}

export interface OrderPerformanceSeries {
  buckets: string[];
  due: number[];
  on_time: number[];
  not_on_time: number[];
  on_time_rate: (number | null)[];
  comparison_rate: KpiComparisonSeries;
}

export interface OrderPerformanceDay {
  date: string;
  business_day: boolean;
  due: number;
  on_time: number;
  not_on_time: number;
  late: number;
  pending: number;
  open: number;
  on_time_rate: number | null;
  mean_delay_days: number | null;
}

export interface OrderPerformanceDimensionRow {
  key: 'marketplace' | 'express';
  label: string;
  marketplace_id: number | null;
  express: boolean | null;
  due: number;
  on_time: number;
  not_on_time: number;
  late: number;
  pending: number;
  open: number;
  on_time_rate: number | null;
  previous_on_time_rate: number | null;
}

export interface OrderPerformanceBlock {
  summary: OrderPerformanceMetrics;
  series: OrderPerformanceSeries;
  /* Détail jour par jour (vide si granularité mois) */
  days: OrderPerformanceDay[];
  by_marketplace: OrderPerformanceDimensionRow[];
  by_express?: OrderPerformanceDimensionRow[];
}

export interface OrderPerformanceResponse {
  period: KpiPeriod;
  generated_at: string;
  /* Données jugées fiables à partir de cette date (comparaisons antérieures masquées) */
  reliable_from: string;
  period_reliable: boolean;
  orders: OrderPerformanceBlock;
  exclusions: {
    exchange: OrderPerformanceBlock;
    hs_proposal: OrderPerformanceBlock;
    out_of_stock: OrderPerformanceBlock;
    /* Lignes non expédiées sans échéance (instantané, indépendant de la période) */
    no_deadline: number;
  };
  sav: OrderPerformanceBlock;
  /* Litiges SAV (refus de prise en charge) : retard assumé, hors taux SAV */
  sav_exclusions: { litigation: OrderPerformanceBlock };
}

export type OrderPerformanceQuery = Pick<KpiQuery, 'start_date' | 'end_date' | 'compare' | 'period_unit'>;

export type OrderPerformanceLineSort =
  | 'deadline'
  | 'shipping_date'
  | 'delay_days'
  | 'marketplace_order_id'
  | 'serial_no'
  | 'status';

export interface OrderPerformanceLinesQuery extends OrderPerformanceQuery {
  scope: OrderPerformanceScope;
  status?: OrderPerformanceStatusFilter;
  /* Jour d'échéance précis (remplace la période) */
  date?: string;
  marketplace_id?: number;
  express?: boolean;
  q?: string;
  page?: number;
  limit?: number;
  sort?: OrderPerformanceLineSort;
  sort_dir?: 'asc' | 'desc';
}

export interface OrderPerformanceLineBase {
  id: number;
  order_line_id: number | null;
  order_id: number | null;
  category: OrderPerformanceScope;
  marketplace_order_id: string | null;
  marketplace: string | null;
  express: boolean;
  deadline: string | null;
  shipping_date: string | null;
  status: OrderPerformanceStatus;
  /* Retard en heures (lignes pas à l'heure seulement) */
  /* Retard en jours calendaires (lignes en retard seulement) */
  delay_days: number | null;
  device_id: number | null;
  serial_no: string | null;
  model_namestring: string | null;
  grade: string | null;
  stock: string | null;
  device_status: string | null;
}

export interface OrderPerformanceOrderLine extends OrderPerformanceLineBase {
  return_id: number | null;
  order_date: string | null;
  sku_okamac: string | null;
  ordered_item: string | null;
  product_description: string | null;
}

export interface OrderPerformanceSavLine extends OrderPerformanceLineBase {
  order_return_id: number;
  stage: OrderPerformanceSavStage;
  stage_label: string;
  reception_date: string | null;
  process_validated_at: string | null;
  urgent: boolean;
}

export type OrderPerformanceLine = OrderPerformanceOrderLine | OrderPerformanceSavLine;

export interface OrderPerformanceLinesResponse {
  scope: OrderPerformanceScope;
  status: OrderPerformanceStatusFilter;
  start_date: string;
  end_date: string;
  page: number;
  limit: number;
  total: number;
  rows: OrderPerformanceLine[];
}
